import {
  buildAttendanceFinalizeDefaults,
  computeAttendancePreview,
  isPaidBookedUser,
} from './attendanceFinalizeUtils';
import { parseTimelineDate } from '../../Events/widgets/timelineDateUtils';
import { formatDate, MOMENT_FORMATS } from '../../../utils/dateFormat';

/** Top → bottom: bright orange through dark brown (Figma RSVP funnel). */
const RSVP_FUNNEL_COLORS = ['#F5A623', '#E98C4A', '#E0680A', '#7A2E0C'];

const formatPercent = (value) => `${Math.round(Number(value) || 0)}%`;

const toCount = (value) => {
  const n = Number(value);
  return Number.isFinite(n) ? Math.max(0, Math.round(n)) : 0;
};

const buildRsvpFunnelStages = (event, defaults) => {
  const funnel = event?.rsvpFunnel ?? {};
  const steps = [
    {
      name: 'Viewed Event',
      count: toCount(event.viewedCount),
    },
    {
      name: "RSVP'd",
      count: toCount(funnel.rsvped ?? defaults.totalRsvps),
    },
    {
      name: 'Paid',
      count: toCount(funnel.paid ?? defaults.paidTickets),
    },
    {
      name: 'Attended',
      count: toCount(funnel.attended ?? defaults.actualAttendance),
    },
  ];

  const maxCount = Math.max(...steps.map((step) => step.count), 1);

  return steps.map((step, index) => ({
    name: step.name,
    count: step.count,
    percentage: maxCount > 0 ? (step.count / maxCount) * 100 : 0,
    fill: RSVP_FUNNEL_COLORS[index] ?? RSVP_FUNNEL_COLORS[RSVP_FUNNEL_COLORS.length - 1],
  }));
};

const getBookingSaleDate = (user) => {
  const raw = user?.outOfPlatform
    ? (user?.createdAt ??
      user?.created_at ??
      user?.bookedAt ??
      user?.booked_at ??
      user?.paidAt ??
      user?.paid_at)
    : (user?.paidAt ?? user?.paid_at ?? user?.bookedAt ?? user?.booked_at);
  return parseTimelineDate(raw);
};

const toLocalDayKey = (date) => [date.getFullYear(), date.getMonth(), date.getDate()].join('-');

const formatTicketTrendDayLabel = (date) => formatDate(date, MOMENT_FORMATS.DATE_SHORT);

/** Daily paid ticket counts from `bookedUsers` (one point per calendar day). */
export const buildTicketSalesTrendFromBookings = (bookedUsers = []) => {
  const salesByDay = new Map();

  bookedUsers.forEach((user) => {
    if (!isPaidBookedUser(user)) {
      return;
    }
    const saleDate = getBookingSaleDate(user);
    if (!saleDate) {
      return;
    }
    const dayKey = toLocalDayKey(saleDate);
    salesByDay.set(dayKey, (salesByDay.get(dayKey) ?? 0) + 1);
  });

  if (!salesByDay.size) {
    return [];
  }

  return [...salesByDay.entries()]
    .sort(([dayA], [dayB]) => dayA.localeCompare(dayB))
    .map(([dayKey, sales]) => {
      const [year, month, day] = dayKey.split('-').map(Number);
      return {
        name: formatTicketTrendDayLabel(new Date(year, month, day)),
        sales,
      };
    });
};

/** View model for partner event Statistics tab (summary cards + charts only). */
export const buildPartnerEventStatisticsModel = (event = {}) => {
  const defaults = buildAttendanceFinalizeDefaults(event);
  const preview = computeAttendancePreview(defaults);
  const attendanceRate =
    event?.attendanceRate != null && !defaults.isFinalized
      ? formatPercent(event.attendanceRate)
      : formatPercent(preview.overallAttendanceRate);

  return {
    summaryCards: [
      {
        id: 'total-rsvps',
        title: 'Total RSVPs',
        value: String(defaults.totalRsvps),
        iconKey: 'rsvps',
        iconBg: '#E8F4FD',
        iconColor: '#1E88E5',
      },
      {
        id: 'paid-attendees',
        title: 'Paid Attendees',
        value: String(defaults.paidTickets),
        iconKey: 'paid',
        iconBg: '#E8F5E9',
        iconColor: '#2E7D32',
      },
      {
        id: 'no-shows',
        title: 'No-Show Count',
        value: String(defaults.noShows),
        iconKey: 'noShow',
        iconBg: '#FFEBEE',
        iconColor: '#C62828',
      },
      {
        id: 'attendance-rate',
        title: 'Attendance Rate',
        value: attendanceRate,
        iconKey: 'attendance',
        iconBg: '#FFF8E1',
        iconColor: '#F9A825',
      },
    ],
    funnelStages: buildRsvpFunnelStages(event, defaults),
    ticketSalesTrend: buildTicketSalesTrendFromBookings(event?.bookedUsers),
  };
};
